import { CSRF_TOKEN_HEADER, SERVER_API_PATH } from "./api-constants";
import { ServerApiDetailResponseType, reportApiError, serverApi } from "./api-server-actions";

import {
  AIContextPartsResponse,
  AISchemaId,
  AISchemaRequestOptionsType,
  AISchemaResponseTypesById,
  AISchemaResponseWrapper,
  AISchemasById,
  AIServerRequestOptionsType,
  AIStreamingRequestOptionsType,
  SSEFunctionCallOutputDataType,
  SSETextDeltaDataType,
  SSEToolCallDataType,
} from "components/server-types";
import { t } from "i18n/i18n.ts";
import { getCsrfToken } from "utils/auth";

/**
 * Perform an AI query to get a JSON response, using a specified schema to constrain
 * the expected response format. Queries are performed synchronously.
 */
export async function serverGetAISchemaResponse<T extends AISchemaId>(
  options: AISchemaRequestOptionsType<T>,
): Promise<AISchemaResponseWrapper<AISchemaResponseTypesById[T]>> {
  // Issue the request to run the given query and obtain a JSON response.
  const url = `${SERVER_API_PATH}/ai/query/`;
  const schema = AISchemasById[options.schemaId];
  const data: AIServerRequestOptionsType = {
    section_id: options.section_id,
    page_id: options.page_id ?? null,
    submission_id: options.submission_id ?? null,
    model: options.model,
    prompt: options.prompt,
    files: options.files || [],
    schema,
  };
  type ResponseType = AISchemaResponseWrapper<AISchemaResponseTypesById[T]>;
  const response = await serverApi.post<ResponseType>(url, data);

  // Check for errors.
  if (response.data.type !== "json") {
    throw new Error(`serverGetAISchemaResponse: Invalid response type: ${response.data.type}`);
  }

  return response.data;
}

/**
 * Perform an AI query to get a response in the form of an event stream, where each event may
 * be either a delta (i.e., a chunk of text) or a tool call. In theory deltas and tool calls
 * can be intermingled, but in practice a stream will consist of only one or the other.
 */
export async function serverGetAIEventStreamResponse(
  options: AIStreamingRequestOptionsType,
): Promise<void> {
  // Issue the request to run the given query and obtain a streaming response.
  const subpath = options.mode === "summarize-responses" ? "summarize-responses" : "chat";
  const url = serverApi.getUri({ url: `${SERVER_API_PATH}/ai/${subpath}/` });
  const data: AIServerRequestOptionsType = {
    section_id: options.section_id,
    page_id: options.page_id ?? null,
    model: options.model,
    prompt: options.prompt,
    files: options.files || [],
    stream: true,
    ...(options.mode === "chat"
      ? {
          tools: options.tools ?? [],
          share_submissions: options.share_submissions,
          doc_access_id: options.doc_access_id ?? null,
          assessing: options.assessing,
        }
      : {}),
  };
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers[CSRF_TOKEN_HEADER] = csrfToken;
  }
  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(data),
  });

  // Check for errors.
  if (!response.ok || !response.body) {
    throw new Error(await response.text());
  }

  // The response will come as a stream of data that can be accessed by way of a reader
  // object. Each chunk of data yielded by the reader will be a byte array that has to
  // be decoded to UTF-8 before processing.
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let emptyResponse = true;
  // SSE frames end with \n\n. A single read() chunk can split mid-frame (e.g. large
  // `event: context` JSON); buffer until we have a full frame before JSON.parse.
  let sseBuffer = "";

  /* eslint-disable no-await-in-loop -- SSE: chunks and frames are processed strictly in order */
  let { done, value } = await reader.read();
  while (!done) {
    if (value) {
      const chunk = decoder.decode(value as AllowSharedBufferSource, { stream: true });
      sseBuffer += chunk;
      const frames = sseBuffer.split("\n\n");
      // Last segment has no trailing \n\n yet — it may be an incomplete next frame;
      // keep it for the next chunk. Only earlier segments are full SSE messages.
      sseBuffer = frames.pop() ?? "";
      for (const frame of frames) {
        if (frame.trim()) {
          // Await so each frame is processed in order: a `function_call` (onToolCalls) must
          // finish before the next frame (e.g. `function_call_output`). Do not await
          // `onToolCallOutput` — it may recurse into a new request; awaiting would block
          // later outputs in the same stream from dispatching and break multi-tool server runs.
          await processServerSentEvent(frame, options);
        }
      }
      emptyResponse = false;
    }
    ({ done, value } = await reader.read());
  }
  /* eslint-enable no-await-in-loop */

  // End of stream: flush any UTF-8 bytes held across the last chunk boundary.
  sseBuffer += decoder.decode();
  if (sseBuffer.trim()) {
    await processServerSentEvent(sseBuffer, options);
  }
  if (emptyResponse) {
    options.onTextDelta(t("error.toasts.get_ai_response_failure"));
  }
}

/**
 * Process one full SSE message. Awaited by the read loop so handlers run in strict order.
 *
 * 1. event: text_delta — data: { "delta": "..." }
 * 2. event: function_call — data: [ { "name", "arguments", "call_id", "server_execution"? } ]
 * 3. event: function_call_output — data: { "call_id", "output", "error"? }
 *
 * `onToolCallOutput` is scheduled (not awaited) so multiple server tool outputs in one
 * stream can all be applied to state before any recursive continuation runs.
 */
async function processServerSentEvent(
  message: string,
  options: AIStreamingRequestOptionsType,
): Promise<void> {
  const lines = message.split("\n");
  const event = lines[0].slice("event:".length).trim();
  const rawData: unknown = JSON.parse(lines[1].slice("data:".length).trim());
  if (event === "context") {
    // rawData from JSON.parse is any; we assert the shape from the server

    const data = rawData as AIContextPartsResponse;
    options.onContext?.(data.parts);
  }
  if (event === "text_delta") {
    const data = rawData as SSETextDeltaDataType;
    options.onTextDelta(data.delta);
  }
  if (event === "function_call") {
    const calls = rawData as SSEToolCallDataType[];
    await options.onToolCalls(calls);
  }
  if (event === "function_call_output") {
    const data = rawData as SSEFunctionCallOutputDataType;
    // Do not await — must not block the loop (see JSDoc). Handler may be sync; if it
    // returns a Promise, it must self-handle rejections.
    options.onToolCallOutput(data);
  }
}

/**
 * Tell the server to refresh its Gitbook-based RAG retrievers. Admin-only.
 */
export async function serverRefreshGitbook(): Promise<ServerApiDetailResponseType> {
  try {
    const response = await serverApi.post<ServerApiDetailResponseType>(
      `${SERVER_API_PATH}/ai/refresh-gitbook/`,
    );
    return response.data;
  } catch (error) {
    reportApiError("serverRefreshGitbook", error);
    throw error;
  }
}
